import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "../../context/ThemeContext";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { Info, AlertCircle, Copy } from "lucide-react";

const MessageSegmentTool = () => {
  useTheme();
  const [message, setMessage] = useState("");
  const [encoding, setEncoding] = useState("auto");
  const [smartEncoding, setSmartEncoding] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [hoveredCharInfo, setHoveredCharInfo] = useState(null);
  
  const [segmentInfo, setSegmentInfo] = useState({
    encoding: "GSM-7",
    segments: 1,
    charactersPerSegment: 160,
    totalCharacters: 0,
    remainingCharacters: 160,
    nonGsmCharacters: [],
    isUnicode: false,
    messageSegments: [],
  });

  // GSM-7 basic character set
  const gsm7BasicChars = useMemo(
    () =>
      new Set([
        "@",
        "£",
        "$",
        "¥",
        "è",
        "é",
        "ù",
        "ì",
        "ò",
        "Ç",
        "\n",
        "Ø",
        "ø",
        "\r",
        "Å",
        "å",
        "Δ",
        "_",
        "Φ",
        "Γ",
        "Λ",
        "Ω",
        "Π",
        "Ψ",
        "Σ",
        "Θ",
        "Ξ",
        "Æ",
        "æ",
        "ß",
        "É",
        " ",
        "!",
        '"',
        "#",
        "¤",
        "%",
        "&",
        "'",
        "(",
        ")",
        "*",
        "+",
        ",",
        "-",
        ".",
        "/",
        "0",
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        ":",
        ";",
        "<",
        "=",
        ">",
        "?",
        "¡",
        "A",
        "B",
        "C",
        "D",
        "E",
        "F",
        "G",
        "H",
        "I",
        "J",
        "K",
        "L",
        "M",
        "N",
        "O",
        "P",
        "Q",
        "R",
        "S",
        "T",
        "U",
        "V",
        "W",
        "X",
        "Y",
        "Z",
        "Ä",
        "Ö",
        "Ñ",
        "Ü",
        "§",
        "¿",
        "a",
        "b",
        "c",
        "d",
        "e",
        "f",
        "g",
        "h",
        "i",
        "j",
        "k",
        "l",
        "m",
        "n",
        "o",
        "p",
        "q",
        "r",
        "s",
        "t",
        "u",
        "v",
        "w",
        "x",
        "y",
        "z",
        "ä",
        "ö",
        "ñ",
        "ü",
        "à",
      ]),
    []
  );

  // GSM-7 extended characters (require escape)
  const gsm7ExtendedChars = useMemo(
    () => new Set(["\f", "^", "{", "}", "\\", "[", "~", "]", "|", "€"]),
    []
  );

  const isGsm7Char = useCallback(
    (char) => {
    return gsm7BasicChars.has(char) || gsm7ExtendedChars.has(char);
    },
    [gsm7BasicChars, gsm7ExtendedChars]
  );

  const containsUnicode = useCallback(
    (text) => {
    for (let i = 0; i < text.length; i++) {
      if (!isGsm7Char(text[i])) {
        return true;
      }
    }
    return false;
    },
    [isGsm7Char]
  );

  const calculateSegments = useCallback(() => {
    let detectedEncoding = "GSM-7";
    let charLimit = 160;
    const nonGsmChars = [];
    
    if (encoding === "auto") {
      if (containsUnicode(message)) {
        detectedEncoding = "UCS-2";
      }
    } else {
      detectedEncoding = encoding;
    }
    
    if (smartEncoding && detectedEncoding === "UCS-2" && encoding === "auto") {
      for (let i = 0; i < message.length; i++) {
        if (!isGsm7Char(message[i]) && !nonGsmChars.includes(message[i])) {
          nonGsmChars.push(message[i]);
        }
      }
      
      if (nonGsmChars.length === 0) {
        detectedEncoding = "GSM-7";
      }
    }
    
    if (detectedEncoding === "UCS-2") {
      charLimit = 70;
    }
    
    const totalChars = message.length;
    let segments = Math.ceil(totalChars / charLimit);
    if (segments === 0) segments = 1;
    
    let remaining = charLimit - (totalChars % charLimit);
    if (remaining === charLimit) remaining = 0;
    
    // Calculate message segments with their characters
    const messageSegments = [];
    for (let i = 0; i < segments; i++) {
      const start = i * charLimit;
      const end = start + charLimit;
      const segmentText = message.slice(start, end);
      messageSegments.push({
        text: segmentText,
        length: segmentText.length,
        isUnicode: detectedEncoding === "UCS-2",
      });
    }
    
    setSegmentInfo({
      encoding: detectedEncoding,
      segments,
      charactersPerSegment: charLimit,
      totalCharacters: totalChars,
      remainingCharacters: remaining,
      nonGsmCharacters: nonGsmChars,
      isUnicode: detectedEncoding === "UCS-2",
      messageSegments,
    });
  }, [message, encoding, smartEncoding, containsUnicode, isGsm7Char]);

  useEffect(() => {
    calculateSegments();
  }, [calculateSegments]);

  const getCharClass = (char) => {
    if (segmentInfo.isUnicode && !isGsm7Char(char)) {
      return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-600 hover:bg-yellow-200 dark:hover:bg-yellow-800";
    } else if (gsm7ExtendedChars.has(char)) {
      return "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-600 hover:bg-blue-200 dark:hover:bg-blue-800";
    }
    return "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600";
  };

  const getCharHex = (char) => {
    return char.charCodeAt(0).toString(16).toUpperCase().padStart(4, "0");
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1500);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  return (
    <>
      <HeaderSection toolId="messageSegmentTool" />
      <main className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <article className="bg-white dark:bg-gray-800 rounded-xl dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white dark:bg-gray-800 dark:shadow-gray-900/10 dark:border-gray-700">
            <h1 className="text-2xl font-bold mb-2">
              SMS Message Segment Calculator
            </h1>
            <p className="opacity-90">
              Calculate SMS message segments and analyze character encoding
            </p>
          </div>

          {/* Full-width message input section */}
          <div className="p-6">
            <div className="flex flex-col space-y-4">
              <label
                htmlFor="message"
                className="block font-medium dark:text-gray-300"
              >
                Message
              </label>
              <textarea
                id="message"
                rows={5}
                className="w-full p-4 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                placeholder="Type your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              {/* Character-by-character display for highlighting */}
              <div
                className="w-full p-4 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono mt-2"
                style={{ minHeight: '80px', overflowY: 'auto' }}
              >
                {message.length > 0 ? (
                  Array.from(message).map((char, index) => (
                    <span
                      key={index}
                      className={`${hoveredCharInfo && hoveredCharInfo.index === index ? 'bg-blue-200 dark:bg-blue-800 rounded px-0.5' : ''}`}
                    >
                      {char}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-400 dark:text-gray-500">Your message will appear here with highlights...</span>
                )}
              </div>
            </div>
          </div>

          {/* Full-width character breakdown section */}
          <div className="px-6 pb-6">
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
              <h2 className="text-base font-semibold dark:text-gray-300 mb-2">Character Breakdown</h2>

              {message.length > 0 ? (
                <div className="flex flex-wrap">
                  {Array.from(message).map((char, index) => (
                    <div
                      key={index}
                      className={`w-12 h-6 border-r border-b ${getCharClass(char)} flex items-center justify-center font-mono text-[10px] last:border-r-0`}
                      title={`Char: ${char === ' ' ? 'Space' : char} | Hex: ${getCharHex(char)}`}
                      onMouseEnter={() => setHoveredCharInfo({ char, index })}
                      onMouseLeave={() => setHoveredCharInfo(null)}
                    >
                      {getCharHex(char)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No characters to display</p>
              )}
            </div>
          </div>

          {/* Message segments (full width) */}
          <div className="px-6 pb-6">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
              <h2 className="text-lg font-semibold dark:text-gray-300 mb-3">
                Message Segments
              </h2>

              {segmentInfo.messageSegments.length > 0 ? (
                <div className="space-y-4">
                  {segmentInfo.messageSegments.map((segment, index) => (
                    <div
                      key={index}
                      className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium dark:text-gray-300">
                          Segment {index + 1}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {segment.length} characters
                          </span>
                          <button
                            onClick={() => copyToClipboard(segment.text, index)}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                            title="Copy segment"
                          >
                            <Copy
                              size={16}
                              className="text-gray-500 dark:text-gray-400"
                            />
                          </button>
                        </div>
                      </div>
                      <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded text-sm font-mono whitespace-pre-wrap break-all dark:text-gray-300">
                        {segment.text}
                      </div>
                      {copiedIndex === index && (
                        <div className="text-xs text-green-600 dark:text-green-400 mt-1 text-right">
                          Copied!
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No segments to display
                </p>
              )}
            </div>
          </div>

          {/* Legend (full width) */}
          <div className="px-6 pb-6">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
              <h2 className="text-lg font-semibold dark:text-gray-300 mb-3">
                Legend
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 mr-2"></div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    GSM-7 Basic Character
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="w-4 h-4 rounded bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-600 mr-2"></div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    GSM-7 Extended Character
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="w-4 h-4 rounded bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-600 mr-2"></div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Non-GSM (Unicode) Character
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="w-4 h-4 rounded bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-center text-xs leading-4 mr-2">
                    ␣
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Space character
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* Two-column layout for the rest of the content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Left column - Settings */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="encoding"
                    className="block mb-2 font-medium dark:text-gray-300"
                  >
                    Encoding
                  </label>
                  <select
                    id="encoding"
                    className="w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={encoding}
                    onChange={(e) => setEncoding(e.target.value)}
                  >
                    <option value="auto">Auto-detect</option>
                    <option value="GSM-7">GSM-7</option>
                    <option value="UCS-2">UCS-2 (Unicode)</option>
                  </select>
                </div>

                <div className="flex items-center mt-6">
                  <input
                    id="smart-encoding"
                    type="checkbox"
                    className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:bg-gray-700"
                    checked={smartEncoding}
                    onChange={(e) => setSmartEncoding(e.target.checked)}
                  />
                  <label
                    htmlFor="smart-encoding"
                    className="ml-2 block text-sm font-medium dark:text-gray-300"
                  >
                    Smart Encoding
                  </label>
                </div>
              </div>
            </div>

            {/* Right column - Segment info */}
            <div className="space-y-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <h2 className="text-lg font-semibold dark:text-gray-300 mb-3">
                  Message Segmentation
                </h2>
                
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Encoding:
                    </span>
                    <span
                      className={`text-sm font-medium ${
                        segmentInfo.isUnicode
                          ? "text-yellow-600 dark:text-yellow-400"
                          : "text-green-600 dark:text-green-400"
                      }`}
                    >
                      {segmentInfo.encoding}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Segments:
                    </span>
                    <span className="text-sm font-medium dark:text-gray-300">
                      {segmentInfo.segments}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Characters per segment:
                    </span>
                    <span className="text-sm font-medium dark:text-gray-300">
                      {segmentInfo.charactersPerSegment}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Total characters:
                    </span>
                    <span className="text-sm font-medium dark:text-gray-300">
                      {segmentInfo.totalCharacters}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Remaining in current segment:
                    </span>
                    <span className="text-sm font-medium dark:text-gray-300">
                      {segmentInfo.remainingCharacters}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Non-GSM characters warning */}
          {segmentInfo.nonGsmCharacters.length > 0 && (
            <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-300 mb-2">
                    Non-GSM Characters Detected
                  </h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400 mb-2">
                    The following characters are not part of the GSM-7 character
                    set and will force Unicode (UCS-2) encoding:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {segmentInfo.nonGsmCharacters.map((char, index) => (
                      <div
                        key={index}
                        className="p-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-600 rounded text-xs"
                      >
                        {char === " " ? "␣" : char} ({getCharHex(char)})
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Information about encoding */}
          <div className="p-6 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-green-600 dark:text-green-400 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                <p>
                  <strong className="text-gray-800 dark:text-gray-200">
                    GSM-7:
                  </strong>{" "}
                  Most common characters fit in this encoding. Messages can be
                  up to 160 characters per segment. If the message is longer
                  than 160 characters, it will be split into multiple segments
                  of 153 characters each.
                </p>
                <p>
                  <strong className="text-gray-800 dark:text-gray-200">
                    UCS-2 (Unicode):
                  </strong>{" "}
                  Required for emojis and many non-Latin scripts. Messages are
                  limited to 70 characters per segment. Longer messages are
                  split into segments of 67 characters each.
                </p>
                <p>
                  <strong className="text-gray-800 dark:text-gray-200">
                    Smart Encoding:
                  </strong>{" "}
                  When enabled, the calculator will attempt to use GSM-7
                  encoding for GSM characters and only switch to UCS-2 for
                  non-GSM characters (like Twilio does).
                </p>
              </div>
            </div>
          </div>
        </article>
      </main>
      <BottomSection toolId="messageSegmentTool" />
    </>
  );
};

export default MessageSegmentTool;
